import { SiRNAProperties } from './enum'
export interface ProductSearch {
  term: string
  count: number
  page: number
  region: string
  locale: string
  facet: string[]
  defaultPurification: number
  defaultSize: number
  accessToken?: string
  store: string
}

export interface SearchResult {
  term: [string]
  count: number
  pageNo: number
  stepNo: number
  perPage: string
  results: [Results]
}

export interface Results {
  refseqId: string
  siRNAType: string
  species: string
  subSetInfo: SubSetInfo[]
}

export interface SubSetInfo {
  siRNAId: string
  geneId: number
  geneSymbol: string
  refseqId: string
  species: string
  method: string
  sirnaProdType: string
  siRNAType: string
  siRNAAntiSenseSeq: string
  siRNASenseSeq: string
  fuzzyStart: number
  siRNATargetSeq: string
  fuzzyEnd: number
  ranking: number
  refseqStart: number
  refseqEnd: number
  price: string
  currency: string
  count: number
  purification: string
  size: string
  material: string
  checked: boolean
  currencySymbol: string
}

export interface SirnaAddToCartResults {
  siRNAId: string
  geneId: string
  geneSymbol: string
  refseqId: string
  species: string
  method: string
  sirnaProdType: string
  siRNAType: string
  siRNAAntiSenseSeq: string
  siRNASenseSeq: string
  fuzzyStart: string
  siRNATargetSeq: string
  fuzzyEnd: string
  ranking: string
  refseqStart: string
  refseqEnd: string
  purification: string
  size: string
}

export interface PurificationValues {
  Standard: string
  HPLC: string
}

export interface Size {
  nmol25: string
  nmol50: string
  nmol10: string
}

export interface PurificationData {
  name: string
  value: string
}

export interface SizeData {
  name: string
  value: string
}

export interface SiRNAData {
  refseqId: string
  siRNAType: string
  species: string
  subSetInfo: SubSetInfo[]
}

export interface Checkboxes {
  mouse: boolean
  human: boolean
  rat: boolean
}

export interface PaginationData {
  totalCount: number
  page: number
  perPageCount: string
}

export interface AddToCartData {
  results: SirnaAddToCartResults[]
  country: string
}

export interface SirnaAddtoCartData {
  results: SirnaAddToCartResults[]
  country: string
  sapNextEnabled: boolean
  locale: string
  timeZone: string
  store: string
}

export interface SiRNAState {
  searchInput: ProductSearch
  searchResult: SearchResult
  siRNAData: [SiRNAData]
  selectedData?: SiRNAData[]
  checkedALL: boolean
  totalPrice: string
  searchError: boolean
  checkboxes: Checkboxes
  paginationData: PaginationData
  pageError: string
  traceOnClick: boolean
  isChipDelete: boolean
  checkboxUpdate: boolean
  stepNo: number
  sirnaAddtoCartData: SirnaAddtoCartData
  geneSearchError: boolean
  perPageCount: string
}

export const initialState: SiRNAState = {
  searchInput: {
    count: 10,
    locale: '',
    page: 1,
    region: '',
    facet: ['species:Human', 'species:Mouse', 'species:Rat'],
    term: '',
    defaultPurification: 1007,
    defaultSize: 1030,
    store: '',
  },
  searchResult: {
    count: 0,
    term: [''],
    pageNo: 0,
    stepNo: 0,
    perPage: '0',
    results: [
      {
        refseqId: '',
        siRNAType: '',
        species: '',
        subSetInfo: [
          {
            siRNAId: '',
            geneId: 0,
            geneSymbol: '',
            refseqId: '',
            species: '',
            method: '',
            sirnaProdType: '',
            siRNAType: '',
            siRNAAntiSenseSeq: '',
            siRNASenseSeq: '',
            fuzzyStart: 0,
            siRNATargetSeq: '',
            fuzzyEnd: 0,
            ranking: 0,
            refseqStart: 0,
            refseqEnd: 0,
            price: '',
            currency: '',
            count: 0,
            purification: '',
            size: '',
            material: '',
            checked: false,
            currencySymbol: '',
          },
        ],
      },
    ],
  },
  siRNAData: [
    {
      refseqId: '',
      siRNAType: '',
      species: '',
      subSetInfo: [
        {
          siRNAId: '',
          geneId: 0,
          geneSymbol: '',
          refseqId: '',
          species: '',
          method: '',
          sirnaProdType: '',
          siRNAType: '',
          siRNAAntiSenseSeq: '',
          siRNASenseSeq: '',
          fuzzyStart: 0,
          siRNATargetSeq: '',
          fuzzyEnd: 0,
          ranking: 0,
          refseqStart: 0,
          refseqEnd: 0,
          price: '',
          currency: '',
          count: 0,
          purification: '',
          size: '',
          material: '',
          checked: false,
          currencySymbol: '',
        },
      ],
    },
  ],
  selectedData: [],
  checkedALL: false,
  totalPrice: '',
  searchError: false,
  checkboxes: {
    human: false,
    mouse: false,
    rat: false,
  },
  paginationData: {
    totalCount: 0,
    page: 1,
    perPageCount: '10',
  },
  pageError: '',
  traceOnClick: false,
  checkboxUpdate: false,
  isChipDelete: false,
  geneSearchError: false,
  perPageCount: '10',
  stepNo: 0,
  sirnaAddtoCartData: {
    results: [],
    country: '',
    sapNextEnabled: false,
    locale: '',
    timeZone: '',
    store: '',
  },
}

type Action =
  | { type: 'INIT' }
  | { type: 'SEARCH_INPUT'; data: ProductSearch }
  | { type: 'UPDATE_SEARCH_RESULT'; data: SearchResult }
  | { type: 'UPDATE_DATA'; data: [SiRNAData] }
  | { type: 'UPDATE_SELECTED_DATA'; data: [Results] | [] }
  | { type: 'UPDATE_CHECKED_ALL'; data: boolean }
  | { type: 'UPDATE_TOTAL_PRICE'; data: string }
  | { type: 'UPDATE_SEARCH_ERROR'; data: boolean }
  | { type: 'UPDATE_CHECK_BOX'; data: Checkboxes }
  | { type: 'UPDATE_PAGINATION_DATA'; data: PaginationData }
  | { type: 'SET_PAGE_ERROR'; data: string }
  | { type: 'SET_INITIAL_STATE'; data: SiRNAState }
  | { type: 'TRACE_ON_CLICK'; data: boolean }
  | { type: 'CHECKBOX_UPDATE'; data: boolean }
  | { type: 'IS_CHIP_DELETE'; data: boolean }
  | { type: 'GENE_SEARCH_ERROR'; data: boolean }
  | { type: 'SET_STEP_NO'; data: number }
  | { type: 'UPDATE_ADD_TO_CART_DATA'; data: SirnaAddtoCartData }
  | { type: 'SET_PER_PAGE_COUNT'; data: number }

export interface ContextProps {
  state: SiRNAState
  dispatch: ({ type, data }) => void
}

export function reducer(state: SiRNAState, action: Action): SiRNAState {
  const keys = {
    SEARCH_INPUT: 'searchInput',
    UPDATE_SEARCH_RESULT: 'searchResult',
    UPDATE_DATA: 'siRNAData',
    UPDATE_SELECTED_DATA: 'selectedData',
    UPDATE_CHECKED_ALL: 'checkedALL',
    UPDATE_TOTAL_PRICE: 'totalPrice',
    UPDATE_SEARCH_ERROR: 'searchError',
    UPDATE_CHECK_BOX: 'checkboxes',
    UPDATE_PAGINATION_DATA: 'paginationData',
    SET_PAGE_ERROR: 'pageError',
    TRACE_ON_CLICK: 'traceOnClick',
    CHECKBOX_UPDATE: 'checkboxUpdate',
    IS_CHIP_DELETE: 'isChipDelete',
    GENE_SEARCH_ERROR: 'geneSearchError',
    SET_STEP_NO: 'stepNo',
    UPDATE_ADD_TO_CART_DATA: 'sirnaAddtoCartData',
    SET_PER_PAGE_COUNT: 'perPageCount',
  }

  if (action.type !== 'INIT') {
    const newState = { ...state }
    newState[keys[action.type]] = action.data
    return newState
  } else {
    return initialState
  }
}

export const configurableOptions = {
  purification: [
    {
      label: SiRNAProperties.Standard,
      value: '1007',
    },
    {
      label: SiRNAProperties.HPLC,
      value: '1009',
    },
  ],
  size: [
    {
      label: SiRNAProperties.Tennmol,
      value: '1030',
    },
    {
      label: SiRNAProperties.Twentynmol,
      value: '1033',
    },
    {
      label: SiRNAProperties.Fiftynmol,
      value: '1034',
    },
  ],
}
